import React from 'react'
import styled, { css } from 'styled-components'
import { ANIMATION_SCALE, device } from '../style'
import Icon from './Icon'
import { IconName } from './IconName'

export type TooltipProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  title?: string
  icon?: IconName
  left: React.CSSProperties['left']
  top: React.CSSProperties['top']
  blockTransform?: boolean
  delay?: number
  onClick?: React.MouseEventHandler
  $active?: boolean
}

const TooltipBubble = styled.div`
  position: absolute;
  width: 15px;
  height: 15px;
  left: -7px;
  top: -7px;
  background-color: white;
  border-radius: 100%;
`

const TooltipLine = styled.div`
  position: absolute;
  left: 0;
  width: 50px;
  height: 1px;
  background: white;

  @media ${device.tablet} {
    width: 100px;
  }
`

const TooltipContent = styled.a`
  position: absolute;
  top: -15px;
  left: 50px;
  height: 30px;
  padding: 0 15px;
  line-height: 30px;
  font-family: DINWebPro-Bold, sans-serif;
  white-space: nowrap;
  background: white;
  border-radius: 25px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
  pointer-events: ${({ href, onClick }) => (href || onClick ? 'auto' : 'none')};

  @media ${device.tablet} {
    top: -20px;
    left: 100px;
    height: 40px;
    padding: 0 20px;
    line-height: 40px;
  }

  ${Icon} {
    display: inline-block;
    height: 34px;
    width: 34px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 100%;
    line-height: 34px;
    text-align: center;
    margin-top: 3px;
    margin-right: -17px;
    margin-left: 15px;
  }
`

const TooltipTip = styled<React.FC<{ title?: string; onClick?: React.MouseEventHandler; icon?: IconName }>>(
  ({ title, onClick, icon, ...props }) => {
    return (
      <div {...props}>
        <TooltipContent onClick={onClick}>
          {title}
          {icon && <Icon name={icon} />}
        </TooltipContent>
        <TooltipBubble />
        <TooltipLine />
      </div>
    )
  }
)`
  position: relative;
  user-select: none;
`

const Tooltip = React.memo(
  styled(
    React.forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      { title, left, top, blockTransform, delay = 0, icon, onClick, ...props },
      ref
    ) {
      return (
        <div {...props} ref={ref}>
          {props.children}
          <TooltipTip title={title} icon={icon} onClick={onClick} />
        </div>
      )
    })
  )`
    position: relative;

    ${({ blockTransform }) =>
      blockTransform
        ? css`
            transform: translate(0, 0) !important;
          `
        : ''}

    ${TooltipTip} {
      position: absolute;
      left: ${({ left }) => left};
      top: ${({ top }) => top};

      & ${TooltipBubble} {
        transform: scale(1);
        transition-duration: ${200 * ANIMATION_SCALE}ms;
        transition-delay: ${({ delay = 0 }) => delay * ANIMATION_SCALE}ms;
        transition-property: transform;
      }

      & ${TooltipLine} {
        transform-origin: left;
        transform: scaleX(1);
        transition-duration: ${100 * ANIMATION_SCALE}ms;
        transition-delay: ${({ delay = 0 }) => (delay + 200) * ANIMATION_SCALE}ms;
        transition-property: transform;
      }

      & ${TooltipContent} {
        transform-origin: left;
        transform: scale(1);
        opacity: 1;
        transition-duration: ${200 * ANIMATION_SCALE}ms;
        transition-delay: ${({ delay = 0 }) => (delay + 300) * ANIMATION_SCALE}ms;
        transition-property: transform, opacity;
      }

      .enter-active & {
        & ${TooltipBubble} {
          transform: scale(0);
          transition-duration: 0ms;
          transition-delay: 0ms;
        }

        & ${TooltipLine} {
          transform: scaleX(0);
          transition-duration: 0ms;
          transition-delay: 0ms;
        }

        & ${TooltipContent} {
          transform: scale(0);
          opacity: 0;
          transition-duration: 0ms;
          transition-delay: 0ms;
          transition-property: transform, opacity;
        }
      }
    }

    ${({ $active }) =>
      $active
        ? css`
            ${TooltipBubble}, ${TooltipLine}, ${TooltipContent} {
              background-color: #cf0000;
              color: #fff;
            }
          `
        : ''}
  `
)

export default Tooltip
